import React, { useEffect, useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Box,
  Stack,
  Chip,
  TextField,
  Button,
  Divider,
  MenuItem,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import heartLoading from "../../../assests/heartLoading.gif";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import dayjs from "dayjs";
import { statusPalette, categoryPalette } from "./FeatureRequestList";
import axiosInstance from "../../../axiosInstanceSetup";
import { useNavigate } from "react-router-dom";
import TagInputUpdate from "./TagInputUpdate";

const PRIMARY_COLOR = "#bb1ccc";
const PRIMARY_DARK = "#000080";
const muiTagColors = [
  { bg: "#DBEAFE", text: "#1E40AF" }, // blue
  { bg: "#DCFCE7", text: "#166534" }, // green
  { bg: "#F3E8FF", text: "#6B21A8" }, // purple
  { bg: "#FFEDD5", text: "#9A3412" }, // orange
];
const assigneeEmails = [
  "ansh.saha@effem.com",
  "anshul.joshi@effem.com",
  "arvind.batham@effem.com",
  "kannan.p@effem.com",
  "madhusudhan.bairy@effem.com",
  "noel.mathewmanoj@effem.com",
  "sachikanta.raul@effem.com",
  "shalini.nayak@effem.com",
  "shubham.raturi@effem.com",
  "amitoz.s.sidhu@effem.com",
  "hardik.talati@effem.com",
  "bhavadharini.ramasamy@effem.com",
];
const containedButtonSx = {
  backgroundColor: PRIMARY_COLOR,
  color: "#fff",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: PRIMARY_DARK,
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "grey.400",
    color: "#f5f5f5",
  },
};
const textButtonSx = {
  color: PRIMARY_COLOR,
  fontWeight: 600,
  "&:hover": {
    backgroundColor: "rgba(0,0,160,0.08)",
  },
};
const outlinedButtonSx = {
  color: PRIMARY_COLOR,
  borderColor: PRIMARY_COLOR,
  "&:hover": {
    borderColor: PRIMARY_DARK,
    backgroundColor: "rgba(0,0,160,0.04)",
  },
};

const inputFocusSx = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "rgba(0,0,160,0.2)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(0,0,160,0.5)",
    },
    "&.Mui-focused fieldset": {
      borderColor: PRIMARY_COLOR,
      borderWidth: 2,
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: PRIMARY_COLOR,
  },
};

const outlinedInputSx = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "rgba(0,0,160,0.2)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(0,0,160,0.5)",
    },
    "&.Mui-focused fieldset": {
      borderColor: PRIMARY_COLOR,
      borderWidth: 2,
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: PRIMARY_COLOR,
  },
};

const STATUS_LABEL_MAP = {
  PENDING_REVIEW: "Pending Review",
  UNDER_REVIEW: "Under Review",
  APPROVED: "Approved",
  IN_PROGRESS: "In Progress",
  COMPLETED: "Completed",
  REJECTED: "Rejected",
};

const formatStatus = (status) =>
  STATUS_LABEL_MAP[status] || status?.replace(/_/g, " ") || "Unknown";

const formatCategory = (category) =>
  category
    ? category.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "Category";

function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

const FeatureRequestDialog = ({
  open,
  onClose,
  request,
  commentValue,
  onCommentChange,
  onSubmitComment,
  adminForm,
  onAdminFormChange,
  onAdminSave,
  saving,
  statusOptions,
  loadingDetails = false,
  submittingComment = false,
  goBack,
}) => {
  const [expandedComments, setExpandedComments] = useState({});
  const [isCommentsExpanded, setIsCommentsExpanded] = useState(false);
  const userRole = localStorage.getItem("role") || ""; //getting the Admin

  const isSuperuser = userRole === "Admin" ? true : false;

  // Domain hierarchy state for onboard product admin controls
  const [hierarchy, setHierarchy] = useState([]);
  const [loadingHierarchy, setLoadingHierarchy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!request) {
      setExpandedComments({});
      return;
    }
    const initialState = (request.comments || []).reduce((acc, comment) => {
      acc[comment.id] = false;
      return acc;
    }, {});
    setExpandedComments(initialState);
  }, [request]);

  // Fetch domain hierarchy when dialog opens and category is REGISTER_PRODUCT
  useEffect(() => {
    if (!open || !isSuperuser || request?.category !== "REGISTER_PRODUCT")
      return;

    let isMounted = true;
    const fetchHierarchy = async () => {
      setLoadingHierarchy(true);
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_URL}/parentdomain_domain_subdomain_list/`,
        );
        if (isMounted) {
          setHierarchy(response.data?.parent_domains || []);
        }
      } catch (error) {
        console.error("Failed to load domain hierarchy", error);
      } finally {
        if (isMounted) {
          setLoadingHierarchy(false);
        }
      }
    };
    fetchHierarchy();
    return () => {
      isMounted = false;
    };
  }, [open, isSuperuser, request?.category]);

  // Initialize product form when request changes
  useEffect(() => {
    if (
      request?.category === "REGISTER_PRODUCT" &&
      request?.productDetails &&
      isSuperuser
    ) {
      onAdminFormChange({
        productName:
          adminForm.productName ?? request.productDetails.productName ?? "",
        ucSchema: adminForm.ucSchema ?? request.productDetails.ucSchema ?? "",
        parentDomain:
          adminForm.parentDomain ?? request.productDetails.parentDomain ?? "",
        domain: adminForm.domain ?? request.productDetails.domain ?? "",
        subDomain:
          adminForm.subDomain ?? request.productDetails.subDomain ?? "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.id, isSuperuser]);

  // Domain hierarchy options
  const parentOptions = useMemo(
    () =>
      hierarchy.map((parent) => ({
        id: parent.parent_domain_id,
        label: parent.parent_domain,
        domains: parent.domains || [],
      })),
    [hierarchy],
  );

  const domainOptions = useMemo(() => {
    const allDomains = parentOptions.flatMap((parent) =>
      parent.domains.map((domain) => ({
        id: domain.domain_id,
        label: domain.domain,
        parentId: parent.id,
        parentName: parent.label,
        subDomains: domain.sub_domains || [],
      })),
    );
    // Filter by selected parent domain name
    if (adminForm.parentDomain) {
      return allDomains.filter(
        (domain) => domain.parentName === adminForm.parentDomain,
      );
    }
    return allDomains;
  }, [parentOptions, adminForm.parentDomain]);

  const subDomainOptions = useMemo(() => {
    const allSubDomains = domainOptions.flatMap((domain) =>
      domain.subDomains.map((sub) => ({
        id: sub.sub_domain_id,
        label: sub.sub_domain,
        domainId: domain.id,
        domainName: domain.label,
        parentId: domain.parentId,
        parentName: domain.parentName,
      })),
    );
    // Filter by selected domain name
    if (adminForm.domain) {
      return allSubDomains.filter((sub) => sub.domainName === adminForm.domain);
    }
    if (adminForm.parentDomain) {
      return allSubDomains.filter(
        (sub) => sub.parentName === adminForm.parentDomain,
      );
    }
    return allSubDomains;
  }, [domainOptions, adminForm.domain, adminForm.parentDomain]);

  // Domain change handlers
  const handleParentDomainChange = (event) => {
    const parentName = event.target.value;
    onAdminFormChange({
      parentDomain: parentName,
      domain: "",
      subDomain: "",
    });
  };

  const handleDomainChange = (event) => {
    const domainName = event.target.value;
    const domain = domainOptions.find((opt) => opt.label === domainName);
    onAdminFormChange({
      domain: domainName,
      parentDomain: domain?.parentName || adminForm.parentDomain,
      subDomain: "",
    });
  };

  const handleSubDomainChange = (event) => {
    const subDomainName = event.target.value;
    const subDomain = subDomainOptions.find(
      (opt) => opt.label === subDomainName,
    );
    onAdminFormChange({
      subDomain: subDomainName,
      domain: subDomain?.domainName || adminForm.domain,
      parentDomain: subDomain?.parentName || adminForm.parentDomain,
    });
  };

  if (!request) return null;

  const toggleComment = (commentId) => {
    setExpandedComments((prev) => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  const priorityLabel = request.priority
    ? request.priority.charAt(0) + request.priority.slice(1).toLowerCase()
    : "Not set";
  const etaLabel = request.eta
    ? dayjs(request.eta).format("MMM D, YYYY")
    : "Not set";
  const upvoteLabel = `${request.upvotes ?? 0} Upvotes`;

  const priorityStyles =
    request.priority === "HIGH"
      ? { bg: "rgba(239,68,68,0.12)", color: "#B91C1C" }
      : request.priority === "MEDIUM"
        ? { bg: "rgba(249,115,22,0.12)", color: "#C2410C" }
        : { bg: "rgba(107,114,128,0.12)", color: "#374151" };

  const renderCommaSeparatedChips = (value) => {
    if (!value) return "-";

    const items = value
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);

    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap">
        {items.map((item, index) => {
          const color = muiTagColors[index % muiTagColors.length];

          return (
            <Tooltip title={item} arrow placement="top">
              <Chip
                key={item}
                label={item}
                size="small"
                sx={{
                  backgroundColor: color.bg,
                  color: color.text,
                  fontWeight: 500,
                }}
              />
            </Tooltip>
          );
        })}
      </Stack>
    );
  };

  return (
    <Dialog open={open} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
        <Stack spacing={0.5} flex={1}>
          <Typography variant="h6" fontWeight={600}>
            {request.title}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              label={
                statusPalette[request.status]?.label ||
                formatStatus(request.status)
              }
              size="small"
              sx={{
                fontWeight: 600,
                bgcolor:
                  statusPalette[request.status]?.bg || "rgba(0,0,160,0.08)",
                color: statusPalette[request.status]?.color || PRIMARY_COLOR,
              }}
            />
            <Chip
              label={
                categoryPalette[request.category]?.label ||
                formatCategory(request.category)
              }
              size="small"
              sx={{
                fontWeight: 600,
                bgcolor: categoryPalette[request.category]
                  ? `${categoryPalette[request.category].color}1A`
                  : "rgba(0,0,160,0.08)",
                color: categoryPalette[request.category]?.color || PRIMARY_DARK,
              }}
            />
            <Chip
              label={`Priority: ${priorityLabel}`}
              size="small"
              sx={{
                fontWeight: 600,
                bgcolor: priorityStyles.bg,
                color: priorityStyles.color,
              }}
            />
            <Chip
              label={`ETA: ${etaLabel}`}
              size="small"
              sx={{
                fontWeight: 600,
                bgcolor: "rgba(59,130,246,0.12)",
                color: "#1D4ED8",
              }}
            />
            <Chip
              label={upvoteLabel}
              size="small"
              icon={<ThumbUpIcon sx={{ fontSize: 16 }} />}
              sx={{
                fontWeight: 600,
                bgcolor: "rgba(16,185,129,0.12)",
                color: "#047857",
                "& .MuiChip-icon": { color: "#047857" },
              }}
            />
          </Stack>
        </Stack>

        {goBack ? (
          <IconButton
            onClick={() => {
              onClose();
              navigate("/feature-requests");
            }}
          >
            <CloseIcon />
          </IconButton>
        ) : (
          <IconButton
            onClick={() => {
              onClose();
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <Divider />
      <DialogContent dividers>
        {loadingDetails ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="50vh"
            flexDirection="column"
            gap={2}
          >
            <img
              src={heartLoading}
              alt="Loading..."
              style={{ width : 200}}
            />
            <Typography color="text.secondary" sx={{ fontSize: "14px" }}>
              Loading feature...
            </Typography>
          </Box>
        ) : (
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Description
              </Typography>
              <Typography color="text.secondary">
                {request.description}
              </Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            {request.productDetails && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Product Details
                </Typography>
                <Box
                  sx={{
                    border: "1px solid rgba(0,0,0,0.08)",
                    borderRadius: 2,
                    p: 2,
                    backgroundColor: "#f4f7ff",
                    display: "grid",
                    gap: 1.5,
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                    },
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    <strong>Product Code:</strong>{" "}
                    {request.productDetails.productCode || "-"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Product Name:</strong>{" "}
                    {request.productDetails.productName || "-"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Owner:</strong>{" "}
                    {request.productDetails.productOwner || "-"}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    <strong>Parent Domain:</strong>{" "}
                    {request.productDetails.parentDomain || "-"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Domain:</strong>{" "}
                    {request.productDetails.domain || "-"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Sub Domain:</strong>{" "}
                    {request.productDetails.subDomain || "-"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>UC Schema:</strong>{" "}
                    {renderCommaSeparatedChips(request.productDetails.ucSchema)}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    <strong>UC Schema GCP:</strong>{" "}
                    {renderCommaSeparatedChips(
                      request.productDetails.ucGcpSchema,
                    )}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    <strong>ADLS Path:</strong>{" "}
                    {renderCommaSeparatedChips(request.productDetails.adlsPath)}
                  </Typography>
                </Box>
              </Box>
            )}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontSize: "14px" }}
                gutterBottom
              >
                Comments ({request?.comments?.length || 0})
                <IconButton
                  onClick={() => setIsCommentsExpanded(!isCommentsExpanded)}
                >
                  {isCommentsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Typography>
              {isCommentsExpanded && (
                <Stack spacing={1}>
                  {request.comments?.length ? (
                    request.comments.map((comment) => (
                      <Accordion
                        key={comment.id}
                        expanded={Boolean(expandedComments[comment.id])}
                        onChange={() => toggleComment(comment.id)}
                        disableGutters
                        sx={{
                          border: "1px solid rgba(0,0,0,0.08)",
                          borderRadius: 2,
                          "&:before": { display: "none" },
                          backgroundColor: "#fafafa",
                        }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ width: "100%" }}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              flexWrap="wrap"
                            >
                              <Typography fontWeight={600}>
                                {comment.author || "User"}
                              </Typography>
                              {comment.isAdmin && (
                                <Chip
                                  label="Admin"
                                  size="small"
                                  sx={{
                                    fontWeight: 600,
                                    bgcolor: "rgba(37,99,235,0.12)",
                                    color: "#1d4ed8",
                                  }}
                                />
                              )}
                              <Typography
                                variant="caption"
                                color="text.disabled"
                              >
                                {dayjs(
                                  comment.createdAt || comment.created_at,
                                ).format("MMM D, YYYY h:mm A")}
                              </Typography>
                            </Stack>
                            {!expandedComments[comment.id] && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  mt: 0.5,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  wordBreak: "break-word",
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                {comment.message}
                              </Typography>
                            )}
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Typography
                            color="text.secondary"
                            sx={{
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            {comment.message || "No details provided."}
                          </Typography>
                        </AccordionDetails>
                      </Accordion>
                    ))
                  ) : (
                    <Typography color="text.secondary">
                      No comments yet. Start the discussion!
                    </Typography>
                  )}
                </Stack>
              )}
              <Stack spacing={1.5} mt={2}>
                <TextField
                  multiline
                  minRows={3}
                  fullWidth
                  placeholder="Add a comment"
                  value={commentValue}
                  onChange={(e) => onCommentChange(e.target.value)}
                  disabled={submittingComment}
                  sx={inputFocusSx}
                />
                <Button
                  variant="contained"
                  onClick={onSubmitComment}
                  disabled={!commentValue.trim() || submittingComment}
                  sx={{
                    ...containedButtonSx,
                    alignSelf: { xs: "stretch", sm: "flex-start" },
                  }}
                  startIcon={
                    submittingComment ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : null
                  }
                >
                  {submittingComment ? "Adding..." : "Add Comment"}
                </Button>
              </Stack>
            </Box>

            {isSuperuser && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Admin Controls
                </Typography>
                <Stack spacing={2}>
                  <Stack spacing={2} direction={"row"}>
                    <TextField
                      label="Status"
                      select
                      value={adminForm.status}
                      onChange={(e) =>
                        onAdminFormChange({ status: e.target.value })
                      }
                      fullWidth
                    >
                      {statusOptions
                        .filter((item) => item.value !== "ALL")
                        .map((status) => (
                          <MenuItem key={status.value} value={status.value}>
                            {status.label}
                          </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                      label="Priority"
                      select
                      value={capitalize(adminForm.priority)}
                      onChange={(e) =>
                        onAdminFormChange({ priority: e.target.value })
                      }
                      fullWidth
                    >
                      {["High", "Medium", "Low"].map((p) => (
                        <MenuItem key={p} value={p}>
                          {p}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      fullWidth
                      label="ETA"
                      type="date"
                      value={adminForm.eta ? adminForm.eta?.split("T")[0] : ""}
                      onChange={(e) =>
                        onAdminFormChange({ eta: e.target.value })
                      }
                      InputLabelProps={{ shrink: true }}
                      sx={outlinedInputSx}
                    />
                    {/* Assigned To field: editable for admin, view-only for others */}
                    {isSuperuser ? (
                      <Autocomplete
                        fullWidth
                        options={assigneeEmails}
                        value={adminForm.assignedTo || null}
                        onChange={(event, newValue) =>
                          onAdminFormChange({ assignedTo: newValue || "" })
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Assigned To"
                            placeholder="Select or search assignee email"
                            sx={outlinedInputSx}
                          />
                        )}
                        freeSolo
                        sx={{ minWidth: "200px" }}
                      />
                    ) : (
                      <TextField
                        fullWidth
                        label="Assigned To"
                        value={request.assigned_to || "-"}
                        InputProps={{ readOnly: true }}
                        sx={outlinedInputSx}
                      />
                    )}
                  </Stack>

                  {/* Product Fields for Onboard Product category */}
                  {request?.category === "REGISTER_PRODUCT" && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Update Product Details
                      </Typography>
                      <Stack
                        spacing={2}
                        direction={{ xs: "column", md: "row" }}
                      >
                        <TextField
                          fullWidth
                          label="Product Name"
                          value={adminForm.productName || ""}
                          onChange={(e) =>
                            onAdminFormChange({ productName: e.target.value })
                          }
                          sx={outlinedInputSx}
                        />
                        <FormControl fullWidth disabled={loadingHierarchy}>
                          <InputLabel id="admin-parent-domain-label">
                            Parent Domain
                          </InputLabel>
                          <Select
                            labelId="admin-parent-domain-label"
                            label="Parent Domain"
                            value={adminForm.parentDomain || ""}
                            onChange={handleParentDomainChange}
                          >
                            <MenuItem value="">
                              <em>
                                {loadingHierarchy
                                  ? "Loading..."
                                  : "Select parent domain"}
                              </em>
                            </MenuItem>
                            {parentOptions.map((option) => (
                              <MenuItem key={option.id} value={option.label}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Stack>
                      <Stack
                        spacing={2}
                        direction={{ xs: "column", md: "row" }}
                      >
                        <FormControl
                          fullWidth
                          disabled={loadingHierarchy || !domainOptions.length}
                        >
                          <InputLabel id="admin-domain-label">
                            Domain
                          </InputLabel>
                          <Select
                            labelId="admin-domain-label"
                            label="Domain"
                            value={adminForm.domain || ""}
                            onChange={handleDomainChange}
                          >
                            <MenuItem value="">
                              <em>
                                {loadingHierarchy
                                  ? "Loading..."
                                  : adminForm.parentDomain &&
                                      !domainOptions.length
                                    ? "No domains available"
                                    : "Select domain"}
                              </em>
                            </MenuItem>
                            {domainOptions.map((option) => (
                              <MenuItem key={option.id} value={option.label}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl
                          fullWidth
                          disabled={
                            loadingHierarchy || !subDomainOptions.length
                          }
                        >
                          <InputLabel id="admin-subdomain-label">
                            Sub Domain
                          </InputLabel>
                          <Select
                            labelId="admin-subdomain-label"
                            label="Sub Domain"
                            value={adminForm.subDomain || ""}
                            onChange={handleSubDomainChange}
                          >
                            <MenuItem value="">
                              <em>
                                {loadingHierarchy
                                  ? "Loading..."
                                  : adminForm.domain && !subDomainOptions.length
                                    ? "No sub domains available"
                                    : "Select sub domain"}
                              </em>
                            </MenuItem>
                            {subDomainOptions.map((option) => (
                              <MenuItem key={option.id} value={option.label}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Stack>
                      <TagInputUpdate
                        label="UC Schema (optional)"
                        value={adminForm.ucSchema}
                        onChange={(e) =>
                          onAdminFormChange({ ucSchema: e.target.value })
                        }
                        helperText="UC Schema (optional) type and press enter"
                      />
                      <TagInputUpdate
                        label="UC Schema GCP (optional)"
                        value={adminForm.ucGcpSchema}
                        onChange={(e) =>
                          onAdminFormChange({ ucGcpSchema: e.target.value })
                        }
                        helperText="UC Schema GCP (optional) type and press enter"
                      />

                      <TagInputUpdate
                        label="ADLS Path (optional)"
                        value={adminForm.adlsPath}
                        onChange={(e) =>
                          onAdminFormChange({ adlsPath: e.target.value })
                        }
                        helperText="ADLS Path (optional) type and press enter"
                      />
                    </>
                  )}
                </Stack>
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent="flex-start"
                  sx={{ pt: 1 }}
                >
                  <Button
                    variant="contained"
                    onClick={onAdminSave}
                    disabled={saving}
                    sx={containedButtonSx}
                  >
                    Save Changes
                  </Button>
                </Stack>
              </Box>
            )}

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid rgba(0,0,0,0.08)",
                backgroundColor: "#f9fafb",
              }}
            >
              {isSuperuser ? (
                <Stack spacing={1}>
                  <TextField
                    fullWidth
                    label="Submitted By"
                    value={adminForm.submittedBy || ""}
                    onChange={(e) =>
                      onAdminFormChange({ submittedBy: e.target.value })
                    }
                    sx={outlinedInputSx}
                    size="small"
                    placeholder="Enter submitter name or email"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Submitted on {dayjs(request.createdAt).format("MMM D, YYYY h:mm A")}
                    {request.modifiedBy && <> &nbsp;|&nbsp; Last modified by <strong>{request.modifiedBy}</strong>{request.modifiedDate && <> on {dayjs(request.modifiedDate).format("MMM D, YYYY h:mm A")}</>}</>}
                  </Typography>
                </Stack>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Submitted by <strong>{request.submittedBy || "Unknown"}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    on {dayjs(request.createdAt).format("MMM D, YYYY h:mm A")}
                  </Typography>
                  <Typography>
                  {request.modifiedBy && (
                    <>Last modified by <strong>{request.modifiedBy}</strong>{request.modifiedDate && <> on {dayjs(request.modifiedDate).format("MMM D, YYYY h:mm A")}</>}</>
                  )}
                  </Typography>
                </>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
      {/* <DialogActions sx={{ justifyContent: "flex-end" }}>
        <Button onClick={onClose} sx={textButtonSx}>
          Close
        </Button>
      </DialogActions> */}
    </Dialog>
  );
};

export default FeatureRequestDialog;
